import type { EventSummary } from '@mmef/shared/api';

/**
 * Filter suggestions taken from the events actually on screen.
 *
 * <p>A free-text box asks people to guess what might match, and rewards a wrong guess with an
 * empty list. Offering the words that genuinely occur in these results means every suggestion
 * returns something, and it's why this returns nothing when the results share no vocabulary —
 * the filter is then hidden rather than shown empty.
 *
 * <p><b>Titles only, not descriptions.</b> Titles are short and curated ("Over-35s Muslim Marriage
 * Event"); descriptions are marketing prose, and mining them produces chips like "welcome" and
 * "evening" that match everything and mean nothing.
 */

/** Grammar, plus words so common in this catalogue that they'd match nearly every event. */
const STOPWORDS = new Set([
  'the', 'and', 'for', 'with', 'a', 'an', 'of', 'in', 'on', 'at', 'to', 'from', 'by', 'or',
  'event', 'events', 'muslim', 'muslims', 'uk', 'new', 'our', 'your', 'you',
]);

/** Under this many results, a word appearing once is still a useful distinction. */
const SMALL_RESULT_SET = 4;
const MAX_SUGGESTIONS = 8;

function tokenise(title: string): string[] {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9+\s-]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 2 && !STOPWORDS.has(word));
}

/**
 * Counts how many *events* contain each term, not how many times it occurs. One event repeating
 * "matchmaking" five times shouldn't outrank a term that genuinely spans five events.
 */
export function deriveKeywords(events: EventSummary[]): string[] {
  if (events.length === 0) return [];

  const documentFrequency = new Map<string, number>();

  for (const event of events) {
    // What the organiser actually said, when they said anything. An explicit "sisters only" beats
    // any amount of guessing from a title, and it's the same vocabulary subscribers type into
    // their alerts — so a chip here matches an alert there.
    //
    // Falling back to the title for everything else, because most of the catalogue arrives from
    // ticketing platforms with no keywords at all and would otherwise offer no filters.
    const terms = new Set<string>();

    if (event.keywords && event.keywords.length > 0) {
      for (const keyword of event.keywords) {
        terms.add(keyword.toLowerCase());
      }
    } else {
      const words = tokenise(event.title);
      for (const word of words) terms.add(word);

      // Bigrams too: "speed dating" is the phrase people think in, and its halves are useless
      // separately.
      for (let i = 0; i < words.length - 1; i++) {
        terms.add(`${words[i]} ${words[i + 1]}`);
      }
    }

    for (const term of terms) {
      documentFrequency.set(term, (documentFrequency.get(term) ?? 0) + 1);
    }
  }

  const threshold = events.length < SMALL_RESULT_SET ? 1 : 2;
  const surviving = [...documentFrequency.entries()]
    .filter(([, count]) => count >= threshold)
    // A term matching every single result narrows nothing.
    .filter(([, count]) => count < events.length || events.length === 1)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

  // Drop a single word when a phrase containing it is just as common — "speed dating" is a better
  // chip than "speed", and offering both wastes a slot on a duplicate.
  const phrases = surviving.filter(([term]) => term.includes(' '));
  const kept = surviving.filter(([term, count]) => {
    if (term.includes(' ')) return true;
    return !phrases.some(([phrase, phraseCount]) => phrase.includes(term) && phraseCount >= count);
  });

  return kept.slice(0, MAX_SUGGESTIONS).map(([term]) => term);
}

/** Whether an event matches every selected keyword. */
export function matchesKeywords(event: EventSummary, keywords: string[]): boolean {
  if (keywords.length === 0) return true;
  // The organiser's own keywords count as a match even when the words appear nowhere else — that
  // is the whole point of letting them tag an event. Same rule as the server's alert matcher, so
  // filtering here and subscribing there can't disagree about what "speed dating" means.
  const tagged = new Set((event.keywords ?? []).map((k) => k.toLowerCase()));
  const haystack = `${event.title} ${event.organiser ?? ''} ${event.city ?? ''}`.toLowerCase();
  return keywords.every((keyword) => tagged.has(keyword) || haystack.includes(keyword));
}
